import { useId, type ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';

export interface TabItem {
  id: string;
  label: string;
  /** Right-hand count, e.g. the number of calls on a lead. */
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Slot on the right of the tab strip, for a filter or an action. */
  action?: ReactNode;
}

/**
 * Underline tabs. The active state is the one place besides a primary button
 * where the accent appears, which is what makes "where am I" legible at a
 * glance without any chrome.
 *
 * Arrow keys move between tabs, per the WAI-ARIA tabs pattern.
 */
export function Tabs({ items, value, onChange, action }: TabsProps) {
  const baseId = useId();

  const move = (direction: 1 | -1) => {
    const index = items.findIndex((item) => item.id === value);
    if (index === -1) return;
    const next = items[(index + direction + items.length) % items.length];
    if (next) onChange(next.id);
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-border">
      <div role="tablist" className="-mb-px flex gap-1">
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              id={`${baseId}-${item.id}`}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  move(1);
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  move(-1);
                }
              }}
              className={cx(
                'flex items-center gap-1.5 border-b-2 px-2.5 pb-2 text-sm font-medium transition',
                active
                  ? 'border-accent text-fg'
                  : 'border-transparent text-fg-subtle hover:text-fg-muted',
              )}
            >
              {item.label}
              {item.count !== undefined && (
                <span
                  className={cx(
                    'numeric rounded-sm px-1 py-px text-2xs',
                    active ? 'bg-accent-subtle text-accent-text' : 'bg-surface-raised text-fg-faint',
                  )}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {action && <div className="pb-2">{action}</div>}
    </div>
  );
}

export function TabPanel({ children }: { children: ReactNode }) {
  return (
    <div role="tabpanel" className="pt-5">
      {children}
    </div>
  );
}
